import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@material-ui/core";
import { PATHS } from "util/appConstants";
import {
  editZipcodeGroup,
  getZipcodes,
  selectZipcodes,
  selectZipcodeStatus,
  selectZipcodesTimestamp,
} from "redux/slices/zipcodeSlice";
import { getTours, selectTours } from "redux/slices/tourSlice";
import { getSuppliers, selectSuppliers, selectSuppliersStatus } from "redux/slices/supplierSlice";
import ZipcodeForm from "components/Zipcodes/form";
import Navbar from "components/Navbar";
import LightLayout from "components/Shared/LightLayout";
import CustomersNavbar from "components/Masterbar/CustomersBar";
import { getCountryLabel, normalizeCountryCode } from "util/countryDisplay";
import { findZipcodeRowWithCoordinates, resolveZipcodeLngLat } from "util/zipcodeListGroup";

const currentAction = "EDIT";

function parseTourIdFromSearch(search) {
  const raw = new URLSearchParams(search).get("tour_id");
  if (raw == null || raw === "") {
    return null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function parseCountryFromSearch(search) {
  const raw = new URLSearchParams(search).get("country");
  if (raw == null || raw === "") {
    return null;
  }
  const c = String(raw).trim().toUpperCase();
  return c.length === 2 ? c : null;
}

function parseSourceFromSearch(search) {
  const raw = new URLSearchParams(search).get("source");
  return raw ? String(raw).trim() : "";
}

function buildEditSearchParams({ tourId, country }) {
  const params = new URLSearchParams();
  if (country) {
    params.set("country", country);
  }
  if (tourId != null) {
    params.set("tour_id", String(tourId));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

const EditZipcode = () => {
  const { t, i18n } = useTranslation("common");
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { zipcode } = useParams();
  const queryTourId = useMemo(
    () => parseTourIdFromSearch(location.search),
    [location.search]
  );
  const queryCountry = useMemo(
    () => parseCountryFromSearch(location.search),
    [location.search]
  );
  const source = useMemo(() => parseSourceFromSearch(location.search), [location.search]);
  const decodedZipcode = useMemo(() => {
    try {
      return decodeURIComponent(zipcode || "").trim();
    } catch (e) {
      return String(zipcode || "").trim();
    }
  }, [zipcode]);
  const loading = useSelector(selectZipcodeStatus);
  const zipcodes = useSelector(selectZipcodes);
  const timestamp = useSelector(selectZipcodesTimestamp);
  const tours = useSelector(selectTours);
  const suppliers = useSelector(selectSuppliers);
  const suppliersLoading = useSelector(selectSuppliersStatus);

  const dataReady = Boolean(timestamp) && !loading;

  useEffect(() => {
    if (!loading && !timestamp) {
      dispatch(getZipcodes());
    }
  }, [dispatch, loading, timestamp]);

  useEffect(() => {
    if (!tours.length) {
      dispatch(getTours());
    }
  }, [dispatch, tours]);

  useEffect(() => {
    if (!suppliers.length && !suppliersLoading) {
      dispatch(getSuppliers());
    }
  }, [dispatch, suppliers, suppliersLoading]);

  const zipcodeRows = useMemo(() => {
    if (!decodedZipcode || !zipcodes?.length) {
      return [];
    }
    return zipcodes.filter(
      (row) => String(row.zipcode ?? "").trim() === decodedZipcode
    );
  }, [decodedZipcode, zipcodes]);

  const countryChoices = useMemo(() => {
    const byCountry = new Map();
    for (const row of zipcodeRows) {
      const code = normalizeCountryCode(row.country);
      if (!byCountry.has(code)) {
        byCountry.set(code, {
          country: code,
          countryName: getCountryLabel(code, t, i18n),
        });
      }
    }
    return [...byCountry.values()].sort((a, b) =>
      String(a.countryName).localeCompare(String(b.countryName))
    );
  }, [zipcodeRows, t, i18n]);

  const resolvedCountry = useMemo(() => {
    if (!zipcodeRows.length) {
      return null;
    }
    if (queryCountry) {
      const matches = zipcodeRows.some(
        (r) => normalizeCountryCode(r.country) === queryCountry
      );
      if (matches) {
        return queryCountry;
      }
      if (countryChoices.length === 1) {
        return countryChoices[0].country;
      }
      return null;
    }
    if (countryChoices.length === 1) {
      return countryChoices[0].country;
    }
    return null;
  }, [zipcodeRows, queryCountry, countryChoices]);

  const rowsForCountry = useMemo(() => {
    if (resolvedCountry == null) {
      return zipcodeRows;
    }
    return zipcodeRows.filter(
      (row) => normalizeCountryCode(row.country) === resolvedCountry
    );
  }, [zipcodeRows, resolvedCountry]);

  const tourChoices = useMemo(() => {
    const byTour = new Map();
    for (const row of rowsForCountry) {
      const tid = row.tour_id;
      if (tid == null) {
        continue;
      }
      if (!byTour.has(tid)) {
        byTour.set(tid, {
          tour_id: tid,
          tourName: row.Tour?.name ?? String(tid),
        });
      }
    }
    return [...byTour.values()].sort((a, b) =>
      String(a.tourName).localeCompare(String(b.tourName))
    );
  }, [rowsForCountry]);

  const resolvedTourId = useMemo(() => {
    if (!rowsForCountry.length) {
      return null;
    }
    if (queryTourId != null) {
      const matches = rowsForCountry.some((r) => Number(r.tour_id) === queryTourId);
      if (matches) {
        return queryTourId;
      }
      if (tourChoices.length === 1) {
        return tourChoices[0].tour_id;
      }
      return null;
    }
    if (tourChoices.length === 1) {
      return tourChoices[0].tour_id;
    }
    return null;
  }, [rowsForCountry, queryTourId, tourChoices]);

  const needsCountryPick =
    dataReady &&
    zipcodeRows.length > 0 &&
    countryChoices.length > 1 &&
    (queryCountry == null || resolvedCountry == null);

  const needsTourPick = false;

  useEffect(() => {
    if (!dataReady || resolvedCountry == null) {
      return;
    }
    if (queryCountry === resolvedCountry) {
      return;
    }
    history.replace({
      pathname: location.pathname,
      search: buildEditSearchParams({
        country: resolvedCountry,
      }),
    });
  }, [
    dataReady,
    resolvedCountry,
    queryCountry,
    history,
    location.pathname,
  ]);

  const initialValues = useMemo(() => {
    if (!decodedZipcode || !rowsForCountry.length || resolvedCountry == null) {
      return null;
    }
    const supplierIds = [
      ...new Set(rowsForCountry.map((r) => r.supplier_id).filter((x) => x != null)),
    ].sort((a, b) => a - b);
    const coordRow =
      findZipcodeRowWithCoordinates(rowsForCountry, resolvedCountry) ??
      findZipcodeRowWithCoordinates(zipcodeRows, resolvedCountry) ??
      rowsForCountry[0];
    const resolvedCoords = resolveZipcodeLngLat(coordRow);

    // Group the rows of rowsForCountry by tour_id to construct tour_mappings list
    const tourMap = new Map();
    for (const r of rowsForCountry) {
      const rowTimings = (r.ZipcodeDeliveryTimings || [])
        .map((zdt) => {
          const tdt = zdt.ToursDeliveryTiming;
          return tdt ? { tourId: Number(tdt.tour_id), departure: tdt.delivery_timing } : null;
        })
        .filter(Boolean);

      for (const rt of rowTimings) {
        if (!tourMap.has(rt.tourId)) {
          tourMap.set(rt.tourId, new Set());
        }
        if (rt.departure) {
          tourMap.get(rt.tourId).add(rt.departure);
        }
      }

      if (r.tour_id != null) {
        const tid = Number(r.tour_id);
        if (!tourMap.has(tid)) {
          tourMap.set(tid, new Set());
        }
        if (r.departure) {
          tourMap.get(tid).add(r.departure);
        }
      }
    }
    const tourMappings = [...tourMap.entries()].map(([tid, deps]) => ({
      tour_id: tid,
      departures: [...deps],
    }));

    return {
      zipcode: decodedZipcode,
      original_zipcode: decodedZipcode,
      supplier_ids: supplierIds,
      tour_mappings: tourMappings.length ? tourMappings : [{ tour_id: null, departures: [] }],
      current_tour_id: rowsForCountry[0]?.tour_id ?? null,
      country: resolvedCountry,
      current_country: resolvedCountry,
      coordinates: coordRow?.coordinates ?? null,
      latitude: resolvedCoords?.latitude ?? null,
      longitude: resolvedCoords?.longitude ?? null,
    };
  }, [decodedZipcode, resolvedCountry, rowsForCountry, zipcodeRows]);

  const otherCountryChoices = useMemo(
    () => countryChoices.filter((choice) => choice.country !== resolvedCountry),
    [countryChoices, resolvedCountry],
  );

  const onSubmit = async (payload) => {
    const body = {
      supplier_ids: payload.supplier_ids,
      tour_mappings: payload.tour_mappings,
      current_tour_id: payload.current_tour_id ?? initialValues?.current_tour_id,
      current_country: normalizeCountryCode(
        payload.current_country ?? initialValues?.current_country ?? resolvedCountry,
      ),
      country: normalizeCountryCode(payload.country),
    };
    if (payload.zipcode && payload.zipcode !== decodedZipcode) {
      body.new_zipcode = payload.zipcode;
    }
    if (payload.zipcode_locations?.length) {
      body.zipcode_locations = payload.zipcode_locations;
    }
    await dispatch(editZipcodeGroup(decodedZipcode, body));
    if (source === "visualization") {
      const nextZip = (payload.zipcode || decodedZipcode || "").trim();
      const nextCountry = normalizeCountryCode(payload.country ?? resolvedCountry);
      const nextTourId = payload.tour_mappings?.[0]?.tour_id ?? resolvedTourId;
      const params = new URLSearchParams();
      params.set("selected_zipcode", nextZip);
      if (nextCountry) {
        params.set("country", nextCountry);
      }
      if (nextTourId != null) {
        params.set("tour_id", String(nextTourId));
      }
      history.push(`${PATHS.zipcodes.visualization}?${params.toString()}`);
      return;
    }
    history.push(PATHS.zipcodes.root);
  };

  const handlePickCountry = (countryCode) => {
    history.replace({
      pathname: location.pathname,
      search: buildEditSearchParams({ country: countryCode }),
    });
  };

  const handlePickTour = (tourId) => {
    history.replace({
      pathname: location.pathname,
      search: buildEditSearchParams({
        country: resolvedCountry,
        tourId,
      }),
    });
  };

  const isFormReady = Boolean(initialValues && tours.length && suppliers.length);
  const showNotFound = dataReady && !zipcodeRows.length;
  const showLayoutLoading =
    !dataReady || (dataReady && !needsCountryPick && !needsTourPick && !showNotFound && !isFormReady);

  let bodyContent = null;
  if (showNotFound) {
    bodyContent = (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          {t("Zipcode not found")}
        </Typography>
        <Button color="primary" onClick={() => history.push(PATHS.zipcodes.root)}>
          {t("Zipcodes")}
        </Button>
      </Box>
    );
  } else if (needsCountryPick) {
    bodyContent = (
      <Box p={3} maxWidth={480}>
        <Typography variant="h6" gutterBottom>
          {t("Edit Zipcode")} — {decodedZipcode}
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          {t("Select country to edit zipcode")}
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          {countryChoices.map((choice) => (
            <Button
              key={choice.country}
              color="primary"
              style={{ marginBottom: 8 }}
              onClick={() => handlePickCountry(choice.country)}
            >
              {t("Edit for country", { countryName: choice.countryName })}
            </Button>
          ))}
          <Button onClick={() => history.push(PATHS.zipcodes.root)} style={{ marginTop: 8 }}>
            {t("Zipcodes")}
          </Button>
        </Box>
      </Box>
    );
  } else if (needsTourPick) {
    bodyContent = (
      <Box p={3} maxWidth={480}>
        <Typography variant="h6" gutterBottom>
          {t("Edit Zipcode")} — {decodedZipcode}
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          {t("Select tour to edit zipcode")}
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          {tourChoices.map((choice) => (
            <Button
              key={choice.tour_id}
              color="primary"
              style={{ marginBottom: 8 }}
              onClick={() => handlePickTour(choice.tour_id)}
            >
              {t("Edit on tour", { tourName: choice.tourName })}
            </Button>
          ))}
          <Button onClick={() => history.push(PATHS.zipcodes.root)} style={{ marginTop: 8 }}>
            {t("Zipcodes")}
          </Button>
        </Box>
      </Box>
    );
  } else if (isFormReady) {
    bodyContent = (
        <ZipcodeForm
        initialValues={initialValues}
        onSubmit={onSubmit}
        action={currentAction}
        tourList={tours}
        supplierList={suppliers}
        otherCountryChoices={otherCountryChoices}
        onSwitchEditCountry={handlePickCountry}
      />
    );
  }

  return (
    <>
      <Navbar />
      <CustomersNavbar />
      <LightLayout doublebar loading={showLayoutLoading}>
        {bodyContent}
      </LightLayout>
    </>
  );
};

export default EditZipcode;
